/**
 * Outreach v2 — camelCase FE types for the admin Outreach tab (Tasks 22-25).
 * The BE (discovr-backend internal/outreach) is snake_case JSON throughout;
 * `outreachApi.ts` in @discovr/features owns the wire->FE mapping explicitly,
 * the same convention importBatchMutations.ts uses for creator-sheet import.
 */
import { DELIVERABLE_REGISTRY, PLATFORMS, type Platform } from './deliverableRegistry';
import type { ConvertedMoney, CreatorLookupStatus, MetricsStatus, RosterStatus, RowMoney } from './roster';

/** outreach's own state machine (spec §2.1c), one value per roster entry and campaign. The row carries
 *  it as its own field; the FE never derives it. `status` is the roster status and stays `outreach`
 *  for the whole outreach phase. */
export const OUTREACH_STATES = [
  'sourced',
  'contacted',
  'replied',
  'rate_provided',
  'rejected',
  'not_interested',
  'ghosted',
  'bounced',
  'unsubscribed',
  'removed',
] as const;
export type OutreachState = (typeof OUTREACH_STATES)[number];

export function isOutreachState(value: unknown): value is OutreachState {
  return typeof value === 'string' && (OUTREACH_STATES as readonly string[]).includes(value);
}

/** Every state is placed exactly once: a new OutreachState fails to compile here until it is classified. */
const STATE_PHASE: Record<OutreachState, 'progress' | 'stopped'> = {
  sourced: 'progress',
  contacted: 'progress',
  replied: 'progress',
  rate_provided: 'progress',
  rejected: 'stopped',
  not_interested: 'stopped',
  ghosted: 'stopped',
  bounced: 'stopped',
  unsubscribed: 'stopped',
  removed: 'stopped',
};
/** Suppression holds: the send gate hard-refuses these, so the thread opens read-only. Classified per state, like the phase. */
const STATE_SUPPRESSED: Record<OutreachState, boolean> = {
  sourced: false,
  contacted: false,
  replied: false,
  rate_provided: false,
  rejected: false,
  not_interested: false,
  ghosted: false,
  bounced: true,
  unsubscribed: true,
  removed: false,
};

/** The states where the conversation is live and a human can move it forward. */
export const OUTREACH_PROGRESS_STATES: readonly OutreachState[] = OUTREACH_STATES.filter((state) => STATE_PHASE[state] === 'progress');
/** The states that stop outreach. Every thread stays openable; `bounced` and `unsubscribed` open read-only. */
export const OUTREACH_STOPPED_STATES: readonly OutreachState[] = OUTREACH_STATES.filter((state) => STATE_PHASE[state] === 'stopped');
export const OUTREACH_SUPPRESSED_STATES: readonly OutreachState[] = OUTREACH_STATES.filter((state) => STATE_SUPPRESSED[state]);

export const isOutreachProgress = (state: OutreachState): boolean => STATE_PHASE[state] === 'progress';
export const isOutreachSuppressed = (state: OutreachState): boolean => STATE_SUPPRESSED[state];

/** Layer 2b: independent pipeline outcomes, siblings of the state rather than values of it. */
export const OUTREACH_CONTACT_OUTCOMES = ['pending', 'found', 'manual', 'not_found'] as const;
export type OutreachContactOutcome = (typeof OUTREACH_CONTACT_OUTCOMES)[number];

export function isOutreachContactOutcome(value: unknown): value is OutreachContactOutcome {
  return typeof value === 'string' && (OUTREACH_CONTACT_OUTCOMES as readonly string[]).includes(value);
}

/** The roster status a row carries: `outreach` for the whole outreach phase, then whatever the roster moved it to
 *  (the BE also returns entries that already left outreach on the with_brand/removed tabs and on a deep link). */
export type OutreachRowStatus = 'outreach' | RosterStatus | 'accepted_by_creator';

/** Keyed by the union so a status added to it fails to compile here; the wire boundary validates against these keys. */
const OUTREACH_ROW_STATUS_SET: Record<OutreachRowStatus, true> = {
  outreach: true,
  pending: true,
  accepted_by_brand: true,
  accepted_by_admin: true,
  rejected_by_brand: true,
  rejected_by_creator: true,
  rejected_by_admin: true,
  removed_by_admin: true,
  script_pending: true,
  script_review: true,
  script_approved: true,
  content_pending: true,
  content_review: true,
  content_approved: true,
  live_pending: true,
  live: true,
  accepted_by_creator: true,
};

export function isOutreachRowStatus(value: unknown): value is OutreachRowStatus {
  return typeof value === 'string' && Object.hasOwn(OUTREACH_ROW_STATUS_SET, value);
}

/** Mirrors Go's domain.IsPastOutreach: a real status other than outreach and the legacy outreach_* ones. A dashboard
 *  status this build does not know yet defaults to With brand (owner ruling 2026-10-09), so it is not a wire error. */
export function isPastOutreachStatus(value: unknown): value is string {
  return typeof value === 'string' && GO_STATUS_SHAPE.test(value) && value !== 'outreach' && !value.startsWith('outreach_');
}

/** Go's roster statuses are lowercase snake_case; anything else is a contract break, not a new status. */
const GO_STATUS_SHAPE = /^[a-z][a-z0-9_]*$/;

/** The `tab` values the FE sends — a subset of Go's allowlist, which still accepts `rate_provided` for stale clients.
 *  `conversations`, `with_rates` and `everyone` are the CRM's Creators lists (CRM v2 contract §3); `everyone`
 *  keeps the stopped, with-brand and removed creators the Inbox's `all` leaves out. `needs_you` is an overlay on the
 *  state tabs: a row in it also stays in its own tab. */
export type OutreachTab =
  | 'all'
  | 'sourced'
  | 'contacted'
  | 'replied'
  | 'closed'
  | 'rejected'
  | 'ghosted'
  | 'in_conversation'
  | 'stopped'
  | 'with_brand'
  | 'scheduled'
  | 'removed'
  | 'conversations'
  | 'with_rates'
  | 'everyone'
  | 'needs_you';

/** Where a row's audience numbers came from (CRM v2 contract §3). Go reserves `screenshot` but never returns it. */
export const OUTREACH_AUDIENCE_SOURCES = ['connected', 'import', 'discovery'] as const;
export type OutreachAudienceSource = (typeof OUTREACH_AUDIENCE_SOURCES)[number];

export function isOutreachAudienceSource(value: unknown): value is OutreachAudienceSource {
  return typeof value === 'string' && (OUTREACH_AUDIENCE_SOURCES as readonly string[]).includes(value);
}

export type OutreachInboxTab = 'all' | 'contacted' | 'not_contacted' | 'waiting_on_brand' | 'stopped' | 'scheduled' | 'removed' | 'needs_you';

/** The row's brand-decision state (Go RowView.brand_state, 2.9). `none` until the creator is handed to the brand. */
export const OUTREACH_BRAND_STATES = ['none', 'pending', 'approved', 'counter_requested', 'rejected'] as const;
export type OutreachBrandState = (typeof OUTREACH_BRAND_STATES)[number];

export function isOutreachBrandState(value: unknown): value is OutreachBrandState {
  return typeof value === 'string' && (OUTREACH_BRAND_STATES as readonly string[]).includes(value);
}

/** Mirrors Go `WithBrandStatuses` (roster/domain/views.go): the roster statuses the With brand tab shows beside a `rate_provided` outreach row. */
export const OUTREACH_WITH_BRAND_STATUSES: ReadonlySet<OutreachRowStatus> = new Set<OutreachRowStatus>([
  'pending',
  'accepted_by_brand',
  'rejected_by_brand',
  'accepted_by_admin',
  'rejected_by_admin',
  'accepted_by_creator',
  'rejected_by_creator',
]);

export function isWithBrandStatus(status: OutreachRowStatus): boolean {
  return OUTREACH_WITH_BRAND_STATUSES.has(status);
}

/** Mirrors the BE ContactSource enum, phyllo_verified included (M3). */
export type OutreachEmailSource =
  | 'manual'
  | 'phyllo_verified'
  | 'phyllo'
  | 'extracted'
  | 'bio'
  | 'link'
  | 'deobfuscated'
  | 'apify';

/** One of the creator's addresses (Go RowEmail). All mail uses the primary unless a campaign override is set. */
export interface OutreachRowEmail {
  address: string;
  /** Open: a newer backend may name a source this build doesn't know. */
  source: OutreachEmailSource | (string & {});
  confidence: number;
  foundAt?: string;
  primary: boolean;
}

/** The pinned pool mailbox as a row carries it — never the full pool view. */
export interface OutreachRowMailbox {
  id: number;
  address: string;
  displayName: string;
}

/** The newest outbound message on a row (BE RowLastMessage). */
export interface OutreachRowLastMessage {
  id: number;
  kind: string;
  status: OutreachMessageStatus;
  subject?: string;
  snippet?: string;
  heldReason?: string;
  lastError?: string;
  sentAt?: string;
}

export type OutreachIntent = 'quoted_rates' | 'negotiating' | 'interested_no_rates' | 'not_interested' | 'question' | 'auto_reply' | 'unsubscribe';
export type OutreachMatchConfidence = 'token' | 'headers' | 'thread' | 'address' | 'unmatched' | 'manual' | 'auto_reply_recent';
export interface OutreachParsedRate { deliverable: string; amount: number; currency: string; confidence: number; quote?: string; notPitched?: boolean }
/** A figure the agent could not bind to one deliverable; a person attaches it. */
export interface OutreachUnmatchedAmount { amount: number; currency: string; quote: string }
/** One deliverable inside a package and how many of it; amount 0 means the creator offers it free. */
export interface OutreachBundleItem { deliverableId: string; count: number; amount?: number }
/** perUnit: amount is the price of ONE piece, not the total (E9-197). */
export interface OutreachParsedBundle { deliverableIds: string[]; amount: number; currency: string; perUnit?: boolean; items?: OutreachBundleItem[]; freeIds?: string[] }
/** reply is a plain draft (an acknowledgement of a quote, or a reply to mail with no money in it): picking it fills the whole mail and never opens a dialog. */
export type OutreachReplyChipAction = 'confirm_rate' | 'ask_for_rate' | 'answer_question' | 'counter' | 'reply';
export interface OutreachReplyChip {
  action: OutreachReplyChipAction;
  label: string;
  draft: { subject: string | null; body: string };
  source: 'llm' | 'template';
}
export interface OutreachParsed {
  source: 'regex' | 'llm';
  rates: OutreachParsedRate[];
  unmatched: OutreachUnmatchedAmount[];
  bundle?: string;
  bundleDetail?: OutreachParsedBundle;
  phone?: string;
  notes?: string;
  suggestions: OutreachReplyChip[];
}
export interface OutreachDSN { action: string; status: string; kind: 'hard' | 'soft' | 'blocked' | 'delayed' | 'other'; originalMessageId?: string }
export interface OutreachQuote {
  id: number;
  round: number;
  rates: { deliverable: string; amount: number; currency: string; confidence: number; source: string }[];
  createdAt: string;
  /** Human rounds so far, when Go sends it. */
  humanRound?: number;
  /** The creator priced a package: its total, with `rates` listing its deliverables at no amount (admin splits it, R17-21). */
  bundle?: { amount: number; currency: string } & Pick<OutreachParsedBundle, 'perUnit' | 'items' | 'freeIds'>;
}

export interface OutreachFollowupDeferred {
  action: 'none' | 'hold' | 'nudge_after';
  reason: string;
  /** YYYY-MM-DD the creator asked for, or null. */
  notBefore: string | null;
  scheduledAt: string | null;
}

/** Why a follow-up is owed (owner 2026-10-12), derived by Go at read time — never a status. creator_wait counts down
 *  to dueAt and then turns overdue; no_reply arrives only once due (48h after our last email with no reply). */
export type OutreachFollowupKind = 'creator_wait' | 'no_reply' | OutreachAutoFollowupKind;

export interface OutreachFollowup {
  kind: OutreachFollowupKind;
  dueAt: string;
  /** The manual Follow up can be drafted and sent now. */
  due: boolean;
  /** Whole days since dueAt; 0 before it and on its first day. */
  overdueDays: number;
  /** F4 (steps_remaining only): when the next automatic step is due, its 1-based step, and where it stands. */
  nextAt?: string;
  step?: number;
  state?: OutreachAutoFollowupState;
  /** Failed steps_remaining only. */
  reason?: string;
  retryable?: boolean;
  templateStep?: number;
  attempts?: number;
}

// ---- F4: automatic follow-ups (Go #814) ----
/** An automatic template step is still to go: the row counts down to it. Never the manual Follow up pill. */
export type OutreachAutoFollowupKind = 'steps_remaining';
export type OutreachAutoFollowupState = 'scheduled' | 'retrying' | 'queued' | 'failed';
// ---- /F4 ----

/** One platform profile's country (ISO 3166-1 alpha-2) and zone (IANA), each with the source that set it. */
export interface OutreachProfileGeo {
  country?: string;
  countrySource?: string;
  timezone?: string;
  timezoneSource?: string;
}

/** G4c: what an admin chose when approving a draft that has no email yet; it expires 14 days after chosenAt. */
export interface OutreachWaitingSend {
  mode: 'now' | 'scheduled';
  /** Set only when mode is 'scheduled': the absolute instant picked. */
  scheduledFor?: string;
  /** Set only when mode is 'now': the browser zone captured at the click. */
  adminTz?: string;
  mailboxId?: number;
  chosenAt: string;
  expiresAt: string;
}

export interface OutreachRow {
  entryId: string;
  userId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  followers?: number;
  /** The YouTube channel hides its subscriber count, so `followers` is 0 because it is unknown. */
  followersHidden?: boolean;
  /** From the same profile-sync snapshot as `followers` — the Creators-tab
   *  roster's headline avg-views figure (whatever its source: a hand-supplied
   *  value or the metrics job's reel median), and the engagement rate as a
   *  percent (e.g. 8.46 for "8.46%"). Both absent until the sync settles. */
  avgViews?: number;
  engagementRate?: number;
  /** Admin-only (empty for every other role): the creator's profile lookup
   *  verdict and when it settled — the Creators-tab PRIVATE / NOT FOUND pill. */
  lookupStatus?: CreatorLookupStatus;
  lookupCheckedAt?: string;
  /** Admin-only: where the snapshot's ER / avg views came from, for the metrics caption. */
  metricsStatus?: MetricsStatus;
  /** Admin-only: when a 'pending' metrics job started, so a stalled fetch
   *  (isStalledPending) reads "Fetch stalled" instead of "Fetching…" —
   *  exactly like the Creators tab. */
  metricsPendingSince?: string;
  status: OutreachRowStatus;
  /** outreach.state; validated at the wire boundary, so it is never an unknown string past `toOutreachRow`. */
  outreachState: OutreachState;
  /** outreach.contact (Layer 2b). Recorded, not acted on: V1 builds no retry. */
  outreachContact?: OutreachContactOutcome;
  /** Go #711; absent when there has never been a first touch, or from an older Go (then lastMessage decides). */
  firstTouch?: OutreachFirstTouch;
  /** Where mail goes: the campaign override, else the creator's primary. */
  email?: string;
  /** Open: a newer backend may name a source this build doesn't know. */
  emailSource?: OutreachEmailSource | (string & {});
  emailOverride?: string;
  /** Every address the creator has, each with the source that found it. Optional only so partial rows in tests stay valid; toOutreachRow always sets it. */
  emails?: OutreachRowEmail[];
  /** A person chose the primary (an admin edit). No automated find ever replaces any primary. */
  primaryPinned?: boolean;
  hasContact: boolean;
  sendTz?: string;
  /** The creator's country and zone from the profile of the platform they are outreached on, the values the
   *  drawer shows (Go #765). null: that profile knows neither. Absent: an older Go; the pill falls back to sendTz. */
  profileGeo?: OutreachProfileGeo | null;
  scheduledAt?: string;
  lastActivityAt: string;
  syncing: boolean;
  /** A fact has sat unprojected past the reconciler's threshold, so the row is failing to update, not just behind. */
  syncStuck?: boolean;

  /** Message-driven columns. All optional: the BE builds them from one batched
   *  Postgres read plus one pool read, either of which may degrade to nothing
   *  without failing the page. */
  pinnedMailbox?: OutreachRowMailbox;
  nextSendAt?: string;
  /** Empty/absent ⇒ the row falls back to the campaign zone. */
  nextSendZone?: string;
  /** The thread's own zone: its first outbound mail's send zone (the campaign zone if that mail was Scheduled,
   *  the admin's if Send now). Every "N days" count on the thread runs in it; empty/absent ⇒ the campaign zone. */
  threadTz?: string;
  nextFollowupAt?: string;
  lastMessage?: OutreachRowLastMessage;
  lastReply?: { id: number; receivedAt: string; intent?: OutreachIntent; snippet?: string; isAutoReply: boolean };
  /** R-8: an unconfirmed quote outranks a later non-quote reply for the row
   *  header; the thread still shows each message's own intent. */
  openQuoteIntent?: OutreachIntent;
  needsYou: boolean;
  /** The newest real creator reply has no outbound after it. Raises needsYou without an escalation; cleared by sending. */
  awaitingReply?: boolean;
  escalatedAt?: string;
  escalationReason?: string;

  /** The negotiation-driven columns (Outreach v2 Part 2.5, ruling 69). The
   *  BE always sends unread/platform/suppressed (no omitempty on RowView)
   *  and toOutreachRow always defaults them, so they're required here —
   *  Task 22 tightened this back from the types-only foundation task's
   *  deliberate, tracked laxity once it was already touching every row
   *  fixture across packages/ui for its own UI changes. */
  followupsPausedAt?: string;
  unread: boolean;
  importSource?: OutreachImportSource;
  /** The rate the creator and the admin agreed on (ruling 54). basePrice is in `currency` (the currency it was agreed in;
   *  absent = INR). `converted` is Go's INR figure at the current rate, absent for INR or without a fresh rate. */
  agreedDeliverables?: { type: string; basePrice: number; currency?: string; converted?: ConvertedMoney }[];
  /** Go's totals over agreedDeliverables (never summed in the FE). */
  agreedMoney?: RowMoney;
  /** The campaign's platform; `'instagram'` when the BE omits it (v1 is
   *  Instagram-only). */
  platform: string;
  /** Go A-G7: false when the campaign's platform has no contact lookup. `platform` cannot tell (imports hard-code instagram). */
  contactLookupSupported?: boolean;
  contactLookupNote?: string;
  /** G4: the newest first touch is a draft with no address yet and the creator still has none; it waits for an email. */
  draftWaitingEmail?: boolean;
  /** G4c: the stored send choice of an approved first touch that waits for an email; null/absent = a plain draft. */
  waitingSend?: OutreachWaitingSend | null;
  /** G4: the effective address (override, else profile email) is on a suppression list. */
  suppressed: boolean;
  suppressionScope?: 'global' | 'brand' | 'campaign';
  /** The active suppression row behind `suppressed` (its scope is
   *  `suppressionScope`) — what "Remove from suppression" deletes by. */
  suppressionId?: number;
  /** R10-4: set only where Go would run "Not an unsubscribe" (the live suppression is the agent's reading of a reply).
   *  `reopen` lifts it and reopens the stopped row; `lift_only` (R12-4) lifts it and clears Needs you on a row the brand holds. */
  notUnsubscribe?: 'reopen' | 'lift_only';
  /** Ruling 1: the agent's follow-up hint moved (or held) the next nudge; null once superseded. */
  followupDeferred?: OutreachFollowupDeferred | null;
  /** The follow-up signal; null when nothing is owed (the creator has the last word, paused, stopped, handed off). */
  followup?: OutreachFollowup | null;
  /** Who raised the current escalation; '' on facts older than 2.7 (= rule). */
  escalationSource?: 'rule' | 'agent' | '';
  escalationNote?: string;
  /** Go Task 13: the latest escalation_cleared fact — admin email, RFC3339, free-text note. Blanked by Go when a new escalation re-flags. */
  escalationClearedBy?: string;
  escalationClearedAt?: string;
  escalationClearedNote?: string;
  /** Display-only reason on a still-queued message, e.g. 'daily_cap' (Ruling 29). */
  heldReason?: string | null;
  /** Go's human-readable reason for a held newest message (A1); `heldReason` stays the raw code. */
  holdReason?: { code: string; label: string } | null;
  /** 2.9: where the brand stands on a handed-off creator; absent/`none` before the hand-off. */
  brandState?: OutreachBrandState;
  /** 2.9: when the creator was handed to the brand through outreach; absent on a sheet-priced with-brand row. */
  handedOffAt?: string;
  /** 2.9: when the brand last decided (approved / asked for less / declined). */
  brandDecidedAt?: string;
  /** 2.9: when the final confirmation was sent (newest sent `confirm`, Go `confirmed_at`). */
  confirmedAt?: string;
  /** 2.10 (Go A6): the newest outbound message (any kind — `lastMessage.kind`
   *  says which) for the row's Retry. Absent from a pre-A6 Go. */
  lastOutboundStatus?: OutreachMessageStatus;
  lastOutboundMessageId?: number;
  /** CRM v2: sent plus received messages on the entry. null from an older Go that does not project it.
   *  Optional only so partial rows in tests stay valid; toOutreachRow always sets it. */
  messageCount?: number | null;
  /** CRM v2: where the audience numbers came from; null when an older Go omits it or names a source this build does not know.
   *  Optional only so partial rows in tests stay valid; toOutreachRow always sets it. */
  audienceSource?: OutreachAudienceSource | null;
  /** CRM v2: the creator quoted a rate on this entry; null from an older Go that does not project it.
   *  Optional only so partial rows in tests stay valid; toOutreachRow always sets it. */
  hasQuote?: boolean | null;
}

export interface OutreachCounts {
  /** One count per outreach state: counts derive from the entries, there is no stored rollup (spec §2.1c). */
  byState: Record<OutreachState, number>;
  /** Entries ever in outreach now at a non-rejected dashboard status; their roster status, not an outreach state. */
  withBrand: number;
  /** Entries ever in outreach now removed_by_admin or rejected_by_*: the Removed tab's post-outreach half. Disjoint
   *  from byState, which counts only rows still at roster status outreach. */
  removedOrRejected: number;
  scheduled: number;
  /** The Inbox badge (ruling 50), replacing `replied` for that purpose. */
  unread: number;
  /** Go #711: the Not contacted badge and "still to reach out to"; absent from an older Go, which falls back to by_state. */
  notContacted?: number;
  /** Go #711: the Contacted badge; absent from an older Go. */
  inConversation?: number;
  /** CRM v2: entries with at least one message. null from an older Go.
   *  Optional only so partial fixtures stay valid; toOutreachCounts always sets it. */
  conversations?: number | null;
  /** CRM v2: entries with a creator quote or a rate locked right now. null from an older Go.
   *  Optional only so partial fixtures stay valid; toOutreachCounts always sets it. */
  withRates?: number | null;
  /** CRM v2: every creator this campaign's outreach has worked (the All creators list). null from an older Go.
   *  Optional only so partial fixtures stay valid; toOutreachCounts always sets it. */
  everyone?: number | null;
  /** The Needs you badge, an overlay on the state tabs. Absent from an older Go, which reads as 0. */
  needsYou?: number;
}

/** Go #711: the newest first-touch attempt's status. `scheduled` is a queued send a person timed. */
export const OUTREACH_FIRST_TOUCH_STATUSES = ['scheduled', 'queued', 'sending', 'sent', 'failed', 'unknown_delivery', 'held', 'awaiting_approval', 'cancelled'] as const;
export type OutreachFirstTouchStatus = (typeof OUTREACH_FIRST_TOUCH_STATUSES)[number];
export const isOutreachFirstTouchStatus = (value: unknown): value is OutreachFirstTouchStatus =>
  typeof value === 'string' && (OUTREACH_FIRST_TOUCH_STATUSES as readonly string[]).includes(value);

export interface OutreachFirstTouch {
  status: OutreachFirstTouchStatus;
  /** Present for scheduled and queued. */
  scheduledFor?: string;
}

/** ruling 78: 'wizard' is the mode-adapter's own value; 'sheet'/'paste' mirror
 *  the row's Excel/Paste tag. */
export type OutreachImportSource = 'wizard' | 'sheet' | 'paste';

export interface OutreachHandlePreviewCounts {
  total: number;
  ok: number;
  duplicate: number;
  invalid: number;
  alreadyInCampaign: number;
  hasPrice: number;
  wrongPlatform: number;
}

/** POST .../outreach/imports/preview: what a handle list would add. Nothing is written yet. */
export interface OutreachHandlePreview {
  counts: OutreachHandlePreviewCounts;
  /** The handles the commit would import (status ok), in input order. */
  committable: string[];
  /** Every previewed handle's status by lowercase handle ("ok", "already_in_campaign", "has_price", …). */
  statuses?: Record<string, string>;
  /** The email Discovr already stores, by lowercase handle; a handle with none is absent. */
  contactEmails?: Record<string, string>;
  fileToken: string;
  /** Past the token size cap the commit sends `committable` instead of the token. */
  commitWithHandles: boolean;
}

export interface OutreachImportBatch {
  id: number;
  status: 'queued' | 'running' | 'done' | 'failed';
  rowCount: number;
  createdCount: number;
  matchedCount: number;
  skippedCount: number;
  /** P1-33. Undefined from an older backend: unknown, not zero. */
  failedCount?: number;
  /** Roster entry ids for every item the BE processed to 'created' or
   *  'matched' (ImportGet's `items[]`, Task 27's "Send first touch to all")
   *  — an item still 'pending', or one that ended up 'skipped_*'/'failed',
   *  contributes nothing here. Populated once GET .../imports/{id} has been
   *  fetched at least once with items on the response; empty until then. */
  createdEntryIds: string[];
}

export interface OutreachOperation {
  id: string;
  kind: string;
  status: 'running' | 'done';
  total: number;
  appliedCount: number;
  skippedCount: number;
  failedCount: number;
  /** queued/exists are written only by ensure_drafts (no draft yet; the nested draft_initial composes it). */
  outcomes: { itemKey: string; outcome: 'applied' | 'skipped' | 'failed' | 'queued' | 'exists'; reason?: string; messageId?: number; retryable?: boolean }[];
}

// ── F2 wizard: batch first-touch drafting ─────────────────────────────────

/** A running operation from operations/active: counts only, no per-item outcomes. */
export interface OutreachActiveOperation {
  id: string;
  kind: string;
  status: string;
  total: number;
  appliedCount: number;
  skippedCount: number;
  failedCount: number;
}

/** A Bulk send (a wizard drafting batch) not yet sent: one entry per batch, for every admin. */
export interface OutreachBulkSend {
  batchId: string;
  startedBy: string;
  startedAt: string;
  templateId?: number;
  templateName?: string;
  total: number;
  generated: number;
  skipped: number;
  failed: number;
  notYet: number;
  /** Drafting (the batch or a Retry of it) is still going. */
  running: boolean;
  /** Drafting is over and drafts wait for review. */
  ready: boolean;
  /** Every draft this send parked was replaced by a newer one: nothing is left to review here, and it is not counted as ready. */
  replaced: boolean;
  /** How many of the send's creators still have its draft as their current one. */
  liveDrafts: number;
  /** The newest first-touch message id from before the send began: only a draft above it belongs to it. */
  draftFloor: number;
  /** Only on the single-send read. */
  entries?: { entryId: string; username: string; displayName?: string }[];
}

/** One of a creator's first-touch drafts: the current one is the draft Send uses. */
export interface OutreachFirstTouchDraft {
  message: OutreachMessage;
  current: boolean;
  /** A person changed it after the assistant wrote it. */
  edited: boolean;
  writtenBy: 'assistant' | 'you';
}

/** A creator's first-touch drafts, newest first; frozen once a first touch is queued or sent. */
export interface OutreachFirstTouchDrafts {
  drafts: OutreachFirstTouchDraft[];
  frozen: boolean;
}

export interface FirstTouchDraftsInput {
  entryIds: string[];
  /** This batch's template; omitted, the campaign's saved pick. */
  templateId?: number;
  /** Cancels an entry's waiting draft, hand edits included, and drafts it anew. */
  replaceExisting?: boolean;
  /** Drafts creators with no email yet; otherwise they are skipped as no_contact. */
  includeNoEmail?: boolean;
  /** Marks the call as a Retry of that Bulk send, so its progress counts under it. */
  batchId?: string;
}
// ── end F2 ────────────────────────────────────────────────────────────────

export interface OutreachContactPatch {
  /** GLOBAL: adds this address to the creator and makes it the pinned primary. */
  email?: string;
  /** GLOBAL: makes an address already on the creator's list the pinned primary. Never sent with `email`. */
  primaryEmail?: string;
  phone?: string;
  whatsapp?: string;
  /** this entry only. */
  emailOverride?: string | null;
  /** IANA zone; null clears the override. */
  sendTz?: string | null;
}

// ── Templates (BE internal/outreach/templates) ────────────────────────────

export type OutreachTemplateScope = 'global' | 'brand' | 'campaign';
export type OutreachTemplateKind = 'sequence' | 'after_reply' | 'other';

export interface OutreachTemplateStep {
  /** Absent on an input step — the BE assigns ids. */
  id?: number;
  stepNo: number;
  /** Fractional days are allowed (BE *float64). */
  delayDays?: number;
  subject?: string;
  body: string;
  /** The step's formatting (sanitised by the BE); its plain twin is `body`. Absent when written plain. */
  bodyHtml?: string;
  /** Follow-ups only, "HH:MM" in the campaign zone: the time of day this step goes out. Absent = no fixed time. */
  sendTime?: string;
  /** Per step: AI wrote or helped write this text. Set when AI fills the step, kept through hand edits, resent on every save. */
  aiAssisted?: boolean;
  /** Beyond the 2nd follow-up: kept for mail that points at it, never sent automatically. */
  manual?: boolean;
}

/** Advisory template lint (BE templates.LintWarning). Never blocks a save. */
export interface OutreachTemplateLintWarning {
  stepNo: number;
  code: string;
  message: string;
}

export interface OutreachTemplate {
  id: number;
  scope: OutreachTemplateScope;
  brandId?: string;
  campaignId?: string;
  parentTemplateId?: number;
  kind: OutreachTemplateKind;
  name: string;
  steps: OutreachTemplateStep[];
  /** Campaigns whose sequence_template_id points here — the edit-a-shared-row warning. */
  usedBy: number;
  archivedAt?: string;
  createdBy: string;
  updatedAt: string;
  /** Bumped on every step save (Ruling 20); undefined from a pre-2.7 BE. */
  version?: number;
  /** U3: when set, mails from this template carry it instead of the mailbox
   *  signature; null/absent ⇒ the mailbox signature. Snapshotted per sequence. */
  signature?: string | null;
  /** The signature's formatting; absent for a signature written before formatting existed. */
  signatureHtml?: string;
  /** Derived by the BE: true when any step is AI-assisted. */
  aiAssisted?: boolean;
  /** Only on a create/patch response; the list route never carries them. */
  warnings?: OutreachTemplateLintWarning[];
}

export interface OutreachTemplateGroups {
  global: OutreachTemplate[];
  brand: OutreachTemplate[];
  campaign: OutreachTemplate[];
}

export interface OutreachTemplateInput {
  /** Defaults to campaign on the campaign route. */
  scope?: 'campaign' | 'brand';
  kind?: OutreachTemplateKind;
  name: string;
  /** True when the admin left the generated default name alone: the BE then adds " (2)" on a clash instead of refusing. */
  nameIsDefault?: boolean;
  /** Required unless parentTemplateId is set (a fork copies the parent's steps). */
  steps?: OutreachTemplateStep[];
  parentTemplateId?: number;
  /** On a fork, null/absent inherits the parent's signature. */
  signature?: string | null;
  /** When set it is the signature (the BE derives the text and drops links); '' clears it. */
  signatureHtml?: string;
}

export interface OutreachTemplatePatch {
  name?: string;
  steps?: OutreachTemplateStep[];
  /** null clears it; omit to leave it unchanged. */
  signature?: string | null;
  /** When set it replaces the signature; '' clears it. */
  signatureHtml?: string;
  /** Step numbers of manual follow-ups to archive. */
  archiveSteps?: number[];
}

/** One prior step handed to the AI as context (no send time, no delay). */
export interface OutreachFillStepPrior {
  subject?: string;
  bodyHtml: string;
}

/** POST /campaigns/{id}/outreach/templates/fill-step: the AI writes ONE step. Nothing is stored. */
export interface OutreachFillStepRequest {
  stepNo: number;
  stepsTotal: number;
  subject?: string;
  bodyHtml?: string;
  instruction?: string;
  delayDays?: number;
  /** Exactly stepNo - 1 entries. */
  priorSteps: OutreachFillStepPrior[];
}

export interface OutreachFillStepResult {
  /** Null for a follow-up: it threads under the first email. */
  subject: string | null;
  bodyHtml: string;
  signedOff: boolean;
  warnings: string[];
}

/** Step 1 rendered against a real roster entry — no side effect. */
export type OutreachSignatureSource = 'template' | 'mailbox' | 'none';

export interface OutreachTemplatePreview {
  subject: string;
  bodyText: string;
  /** Step 1's formatting rendered for the creator, without the signature; absent when plain. */
  bodyHtml?: string;
  /** Absent from older BEs; the UI then infers it from the template's signature. */
  signatureSource?: OutreachSignatureSource;
}

// ── Per-campaign settings (BE internal/outreach/settings) ──────────────────

/** The region an unset campaign counts as. */
export const DEFAULT_OUTREACH_REGION = 'IN';

export interface OutreachSettings {
  campaignId: string;
  /** false ⇒ the BE answered with defaults, nothing saved yet. */
  exists: boolean;
  sequenceTemplateId?: number;
  askRateFirst: boolean;
  /** IANA zone. */
  sendTz: string;
  walkAwayCeiling?: number;
  maxRounds: number;
  followupDailyCap: number;
  /** F4: the campaign's "Automatic follow-ups" switch; absent reads as ON (the default). */
  autoFollowups?: boolean;
  /** The effective outreach region, an upper-case ISO 3166-1 alpha-2 code ("IN" while none is saved). */
  region: string;
  /** True only when an admin saved a region; false means the India default applies. */
  regionSet: boolean;
  pausedAt?: string;
  updatedAt?: string;
  /** True once any first touch for the campaign was sent: the template is locked (owner 2026-10-12). */
  templateLocked: boolean;
}

/** The PUT body: pause/resume are their own verbs, so pausedAt is not settable.
 *  `ifUnmodifiedSince` is the `updatedAt` the form was loaded with; a mismatch
 *  answers 412 SETTINGS_CHANGED instead of overwriting another session's save. */
export type OutreachSettingsInput = Omit<
  OutreachSettings,
  'campaignId' | 'exists' | 'pausedAt' | 'updatedAt' | 'templateLocked' | 'region' | 'regionSet'
> & {
  /** Left out keeps the stored region; "" clears it back to the India default; a country code sets it. */
  region?: string;
  ifUnmodifiedSince?: string;
};

/** The sequence-template change check (Task 6, P8-6): `dry: true` answers how
 *  many waiting drafts would be regenerated without touching anything — a
 *  minimal `{regenerates}` answer, since nothing was written; `dry: false`
 *  commits the template, triggers that regeneration, and answers the full
 *  settings View (same shape `putOutreachSettings` gets back) alongside
 *  `regenerates`. `settings` carries that commit answer — including the
 *  fresh `updatedAt` the write actually bumped — so the cache can be
 *  reseeded exactly like a normal Save instead of leaving a stale
 *  `updatedAt` behind that would 412 the next write in the same session.
 *  Absent on a dry check. */
export interface OutreachSequenceTemplateCheck {
  regenerates: number;
  settings?: OutreachSettings;
}

// ── Messages (BE internal/outreach/sending) ────────────────────────────────

export type OutreachMessageStatus =
  | 'awaiting_approval'
  | 'queued'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'held'
  | 'cancelled'
  /** Gmail's outcome could not be established — an operator resends or marks it sent. */
  | 'unknown_delivery'
  | 'received';

export type OutreachHeldReason =
  | 'no_contact'
  | 'suppressed'
  | 'entry_not_sendable'
  | 'mailbox_paused'
  | 'mailbox_disabled'
  | 'invalid_send_tz'
  | 'daily_cap'
  | 'recent_contact'
  | 'unfilled_placeholder'
  | 'no_mx'
  | 'role_address'
  | 'disposable_address'
  | 'followup_daily_cap'
  | 'no_thread'
  | 'no_mailbox'
  | 'pacing'
  | 'recipient_not_allowlisted'
  | 'us_postal_address_missing'
  | 'send_now_recovery'
  | 'send_hold'
  | 'campaign_paused'
  | 'auto_followups_off';

export interface OutreachMessage {
  id: number;
  direction: 'out' | 'in';
  kind: string;
  step?: number;
  status: OutreachMessageStatus;
  subject?: string;
  bodyText: string;
  toAddress?: string;
  fromAddress?: string;
  mailboxId?: number;
  requestedMailboxId?: number;
  heldReason?: OutreachHeldReason | string;
  /** A first-touch draft flagged at draft time (role or disposable address): approving it holds it unless sent anyway. */
  addressGuard?: { code: string; label: string };
  lastError?: string;
  /** The cancel answer only: true when Stop put the first touch back as a draft; absent when it only cancelled. */
  draftReturned?: boolean;
  scheduledFor?: string;
  sendTz?: string;
  sendAttemptedAt?: string;
  sentAt?: string;
  receivedAt?: string;
  visibleText?: string;
  intent?: OutreachIntent;
  parsed?: OutreachParsed;
  matchConfidence?: OutreachMatchConfidence;
  candidateEntryId?: string;
  isAutoReply: boolean;
  isDsn: boolean;
  dsn?: OutreachDSN;
  truncated: boolean;
  parseError?: string;
  quote?: OutreachQuote;
  /** Sanitised HTML — inbound only, the creator's own rich content. */
  bodyHtml?: string;
  /** Sanitised HTML — outbound reply/confirm/counter only, the composer's
   *  own rich content. Distinct from bodyHtml: an outbound row's stored
   *  body_html is the branded send envelope, not display content. */
  bodyRichHtml?: string;
  attempts: number;
  actor: string;
  createdAt: string;
  attachments?: AttachmentSummary[];
  /** Who wrote an outbound draft (2.7): null on pre-2.7 rows. */
  draftSource?: OutreachDraftSource | null;
  /** A person changed this draft's text before it was approved. */
  editedBeforeApproval?: boolean;
}

/** Inline thread-bubble chip data — the same rows Task 27's attachment
 *  drawer lists in full, embedded per-message so a chip renders without a
 *  second network round trip per bubble. */
export interface AttachmentSummary {
  id: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /** Q5: an image shown inside the email body (cid:), not attached. Signature
   *  logos never reach the client; a pasted screenshot does, tagged. */
  isInline?: boolean;
}

export interface OutreachQueuedMessage {
  messageId: number;
  status: 'queued';
  scheduledFor?: string;
}

/** The machine-readable batch skip reasons (BE sending.SkipReason). */
export type OutreachSkipReason =
  | 'suppressed'
  | 'removed'
  | 'replied'
  | 'closed'
  | 'campaign_paused'
  | 'mailbox_unavailable'
  | 'no_contact'
  | 'already_contacted'
  | 'not_in_outreach'
  | 'with_brand'
  | 'already_removed'
  | 'has_placeholder'
  | 'on_hold';

/** GET /outreach/status — named for the send pool, not the per-entry
 *  per-entry lifecycle `OutreachState`. */
export interface OutreachSendStatus {
  /** false ⇒ outreach is not configured in this environment. */
  enabled: boolean;
  sendHold: boolean;
  gmailConfigured: boolean;
  mailboxCount: number;
  activeMailboxes: number;
  unmatchedCount: number;
}

/** One pool mailbox as the send dialog's picker needs it — a projection of the
 *  BE MailboxView, whose warmup/bounce/poll fields belong to the admin pool screen. */
export interface OutreachPoolMailbox {
  id: number;
  address: string;
  displayName: string;
  status: string;
  sentToday: number;
  capToday: number;
}

// ── Negotiation, drafts and KPIs (Outreach v2 Part 2.5) ────────────────────

/** SendMessage's whole vocabulary (ruling 53) — 'initial' stays first-touch
 *  only; useOutreachNegotiation never sends it. */
export type OutreachSendKind = 'initial' | 'reply' | 'confirm' | 'counter';

export interface OutreachRate {
  deliverable: string;
  amount: number;
  currency: string;
}

/** body is REQUIRED — the human approved exactly this text (HITL ruling 8). */
export interface OutreachHumanSendInput {
  kind: Exclude<OutreachSendKind, 'initial'>;
  subject?: string;
  body: string;
  /** Sanitised rich-text rendering of `body` from the composer's editor —
   *  omitted when the composer sent plain text only. */
  bodyHtml?: string;
  rates?: OutreachRate[];
  scheduledFor?: string;
  /** The suggest-reply job the body came from (Ruling 4 accept-rate). */
  suggestionJobId?: string;
  /** Sent from the thread's reply box: only that send ends its saved draft. */
  clearComposerDraft?: boolean;
  /** Go #706, kind reply only: the reply came from the due "Follow up" suggestion, so its send re-arms the next step. */
  followup?: boolean;
  /** The text started as this step of the creator's sequence (the Template button): the send is stamped draft_source template. */
  fromTemplateStep?: number;
}

/** A drafted message body the AI wrote. There is no template stand-in: a failure is an error, never text. */
export interface OutreachSuggestion {
  subject: string;
  body: string;
  /** The agent's formatting for `body` (Go #741: follow-up and acknowledgement drafts); absent when it wrote none. */
  bodyHtml?: string;
  jobId?: string;
}

/** One step of the creator's sequence rendered with their merge tags (the Template button). No AI wrote it. */
export interface OutreachTemplateStepText {
  step: number;
  /** A first step only; a follow-up goes in the same thread. */
  subject?: string;
  body: string;
  bodyHtml?: string;
}

/** close's whole vocabulary (ruling 56) — bounced/unsubscribed are system
 *  reasons, never sent by this input. */
/** brand_rejected closes a brand-declined hand-off (2.9 D6). */
export type OutreachCloseReason = 'rejected' | 'not_interested' | 'ghosted' | 'brand_rejected';

/** Fact reasons old closed rows still carry; the fold reads both as ghosted. Display only, never sent. */
export type OutreachLegacyCloseReason = 'unresponsive' | 'no_contact';

/** The admin actions the thread drawer lists beside the messages (E9-93). */
export type OutreachTimelineKind =
  | 'rate_agreed'
  | 'closed'
  | 'reopened'
  | 'removed'
  | 'reinstated'
  | 'followups_paused'
  | 'followups_resumed'
  | 'sent_to_brand';

/** One admin action on an entry: who did it ('system' and 'agent' are not people) and when. `reason` is set on a closed event only. */
export interface OutreachEntryEvent {
  id: number;
  kind: OutreachTimelineKind;
  actor: string;
  /** admin, system or agent. Absent from an older backend. */
  actorKind?: 'admin' | 'system' | 'agent';
  /** What to show: the admin's name (their email when none), or plain words for system and agent actors. */
  actorName?: string;
  at: string;
  reason?: string;
  /** How far an unsubscribe reaches; set on a closed/unsubscribed event only. Absent on older events (read as all Discovr email). */
  scope?: 'campaign' | 'brand' | 'global';
  scopeBrandName?: string;
}

export type OutreachDraftKind = 'initial' | 'followup';
export type OutreachDraftSource = 'agent' | 'template' | 'human';

/** One approval-queue row: an agent-written first touch or follow-up. */
export interface OutreachDraft {
  message: OutreachMessage;
  entry: OutreachRow;
  kind: OutreachDraftKind;
  draftSource: OutreachDraftSource | null;
}

/** The wizard's send choices; Go applies them to `initial` drafts on the bulk approve (Ruling 37).
 *  No zone: Go would record it as each creator's own zone (U2), and `scheduledFor` is already absolute. */
export interface OutreachApproveSendOptions {
  mailboxId?: number;
  scheduledFor?: string;
}

/** Bulk approve; the schedule applies to every approved draft. */
export interface OutreachApproveDraftsInput extends OutreachApproveSendOptions {
  messageIds: number[];
  /** The wizard's Send now: sent at once, so never together with `scheduledFor`. */
  sendNow?: boolean;
  /** The step 3 drafting operation. Go approves a first-touch draft only under a batch (under 24 h old) that lists its creator. */
  wizardBatchId?: string;
}

export interface OutreachDraftPage {
  items: OutreachDraft[];
  total: number;
  page: number;
  limit: number;
}

/** ruling 66 verbatim — the campaign KPI read. */
export interface OutreachDeliverability {
  sent: number;
  bounced: number;
  complaints: number;
}

export interface OutreachKpis {
  replyRate: number;
  /** null when no follow-up has ever been sent ("measured only"). */
  agentAcceptRate: number | null;
  /** Ruling 4: sent-unedited / sent over agent drafts only, per kind; null when none sent. */
  agentAcceptRateByKind: { initial: number | null; followup: number | null; reply: number | null };
  draftsSent: number;
  draftsSentUnedited: number;
  /** U7 (Go A6): this campaign's outbound since creation. `sent`
   *  counts messages; `bounced` counts entries
   *  closed as bounced; `complaints` counts admin-recorded complaint
   *  suppressions attributed to this campaign. Null when Go omits the block
   *  (an older server), so the card shows dashes rather than false zeros. */
  deliverability?: OutreachDeliverability | null;
  generatedAt: string;
  /** CRM v2 (contract §1). Each is null when an older Go omits it. Optional only so partial fixtures stay valid;
   *  toKpis always sets them. */
  funnelSteps?: OutreachFunnelSteps | null;
  creatorsInPlay?: number | null;
  ratesLockedNow?: number | null;
  /** signed ÷ contacted; null until signing is built or while nobody has been contacted. */
  contactToSigned?: number | null;
  stageMix?: OutreachStageMix | null;
  /** How long Go keeps this view cached: a refetch sooner gets it back unchanged. null from an older Go.
   *  Optional only so partial fixtures stay valid; toKpis always sets it. */
  refreshAfterMs?: number | null;
}

/** CRM v2 funnel, at-least semantics: each step is never larger than the one before. */
export interface OutreachFunnelSteps {
  candidates: number;
  reachable: number;
  contacted: number;
  replied: number;
  rateLocked: number;
  withBrand: number;
  /** null until the close is built; never a fabricated 0. */
  signed: number | null;
}

/** CRM v2 stage mix: the five buckets partition every entry exactly once. */
export interface OutreachStageMix {
  notContacted: number;
  inFlight: number;
  inConversation: number;
  lockedSigned: number;
  closed: number;
  stillMoving: number;
  outOfPlay: number;
  total: number;
}

// ── CRM v2: mailbox health (contract §2) ───────────────────────────────────

export const OUTREACH_MAILBOX_STATUSES = ['warming', 'active', 'paused', 'resync', 'disabled'] as const;
export type OutreachMailboxStatus = (typeof OUTREACH_MAILBOX_STATUSES)[number];

export function isOutreachMailboxStatus(value: unknown): value is OutreachMailboxStatus {
  return typeof value === 'string' && (OUTREACH_MAILBOX_STATUSES as readonly string[]).includes(value);
}

/** Which limit leaves the least headroom; `paused` when the mailbox cannot send at all. */
export const OUTREACH_MAILBOX_LIMITS = ['sent_today', 'bounce', 'complaints', 'paused'] as const;
export type OutreachMailboxLimit = (typeof OUTREACH_MAILBOX_LIMITS)[number];

export function isOutreachMailboxLimit(value: unknown): value is OutreachMailboxLimit {
  return typeof value === 'string' && (OUTREACH_MAILBOX_LIMITS as readonly string[]).includes(value);
}

/** `pool` when the campaign has sent nothing yet, so the pool's active mailboxes stand in. */
export type OutreachMailboxHealthSource = 'campaign' | 'pool';

export interface OutreachMailboxHealthItem {
  id: number;
  /** null for a mailbox no longer in the pool: Go reports it by id alone, as disabled. */
  address: string | null;
  domain: string;
  /** null for a status this build does not know. */
  status: OutreachMailboxStatus | null;
  /** null when the mailbox is not paused or Go gave no reason. */
  pausedReason: string | null;
  sentToday: number;
  capToday: number;
  sends7d: number;
  hardBounces7d: number;
  /** null when nothing was sent in the last 7 days. */
  bounceRate7d: number | null;
  bounceLimit: number | null;
  minSendsForBounce: number | null;
  /** Too few sends in 7 days for the bounce rate to mean anything. */
  insufficientVolume: boolean;
  /** null when Postmaster has no row for the domain. */
  domainSpamRate: number | null;
  spamWarn: number | null;
  /** 0–1; null from a payload that omits it. */
  headroom: number | null;
  tightest: OutreachMailboxLimit | null;
}

export interface OutreachMailboxHealth {
  source: OutreachMailboxHealthSource;
  generatedAt: string | null;
  /** Least headroom first, as Go orders them. */
  mailboxes: OutreachMailboxHealthItem[];
}

// ── CRM v2: rates and agreements (contract §4) ─────────────────────────────

/** One amount in its own currency; amounts are never converted. */
export interface OutreachMoney {
  amount: number;
  /** Empty when Go could not name it (a lock with no confirm or quote behind it). */
  currency: string;
}

export interface OutreachRateFormat {
  format: string;
  /** The creator's latest quote for this format. */
  quoted: OutreachMoney | null;
  /** The latest locked rate for this format. */
  locked: OutreachMoney | null;
  /** When the newest quote for this format arrived; null when never quoted. */
  quotedAt: string | null;
  /** How many of the format the figures cover; 1 until the multi-deliverable UI ships. */
  quantity: number;
}

/** One price the creator gave for several deliverables together. */
export interface OutreachRatePackage {
  /** A format repeats once per unit the package names ("2 reels" is the id twice). */
  deliverableIds: string[];
  /** Multiplies the ids; 1 for now. */
  quantity: number;
  price: OutreachMoney;
  quotedAt: string;
}

export interface OutreachRatesRow {
  entryId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  brandState: OutreachBrandState;
  formats: OutreachRateFormat[];
  /** Package prices still standing: no newer quote replaced every format they name. */
  packages: OutreachRatePackage[];
  /** Quoted amounts the parser could not tie to a format or a package. */
  unassignedQuotes: OutreachMoney[];
  /** Rate locks after the first; 0 when locked once or never. */
  revisions: number;
  lockedBy: string | null;
  lockedAt: string | null;
  /** The creator quoted again after the latest lock. */
  quoteAfterLock: boolean;
}

export interface OutreachRatesPage {
  rows: OutreachRatesRow[];
  total: number;
}

/** Every registry deliverable type a human can quote a rate for on a
 *  platform — main + addon, mirrors the BE's DeliverableTypesForPlatform
 *  (ruling 54; internal/roster/domain/deliverable_registry.go). The
 *  universal usage_rights add-on is offered on Instagram only, matching the
 *  BE's OfferedOnPlatform special case (YouTube has no usage-rights toggle
 *  at campaign creation). CounterRatesDialog's "Add deliverable" picker and
 *  Task 25's agree-rate grid both read this. */
/** Mirrors the BE allowlist (internal/outreach/quote/money.go:29). */
export const OUTREACH_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'AUD', 'CAD', 'SGD'] as const;
export type OutreachCurrency = (typeof OUTREACH_CURRENCIES)[number];

/** The parser's placeholder for an amount the creator gave without a deliverable (G7). */
export const UNSPECIFIED_DELIVERABLE = 'unspecified';

export const OUTREACH_DELIVERABLES_BY_PLATFORM: Record<Platform, string[]> = Object.fromEntries(
  PLATFORMS.map((platform) => [
    platform,
    Object.entries(DELIVERABLE_REGISTRY)
      .filter(([, meta]) => meta.platform === platform || (meta.platform === '*' && platform === 'instagram'))
      .map(([type]) => type),
  ]),
) as Record<Platform, string[]>;
